/**
 * useResizeHandle  component
 *
 * @package editor
 * @copyright 2021-2025 Hexio a.s. <contact@hexio.io> (hexio.io) and contributors.
 * @license Apache-2.0
 *
 * See LICENSE file distributed with this source code for more information.
 */

import { useResizeContext } from "./ResizeContext";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { RESIZE_COMPONENT_TYPE, ResizeElementId, ResizeEvent } from "./ResizeTypes";
import { useEventListener } from "@adapptio/lib-editor-ui";
import { isFunction } from "@adapptio/lib-shared";

export interface IResizeHandleProps
	extends Pick<
		React.DetailedHTMLProps<React.HTMLAttributes<HTMLDivElement>, HTMLDivElement>,
		"onMouseDown" | "onTouchStart" | "ref"
	> {
	"data-resize-handle-id": string;
}

export interface IResizeHandleData {
	active: boolean;
	disabled: boolean;
}

export function useResizeHandle(elementId: ResizeElementId): [IResizeHandleProps, IResizeHandleData] {
	if (!elementId) {
		throw new Error(`Invalid elementId: ${elementId}`);
	}

	const { onStartResize, onResize, onStopResize, registerElement, unregisterElement } = useResizeContext();

	const resizeHandleRef = useRef<HTMLDivElement>();
	const [ active, setActive ] = useState(false);
	const [ disabled, setDisabled ] = useState(false);

	const startResize = useCallback<(event: ResizeEvent) => void>(
		(event) => {
			if (!disabled) {
				setActive(true);
				onStartResize(elementId, event, resizeHandleRef.current);
			}
		},
		[ elementId, onStartResize, disabled ]
	);

	const resize = useCallback<(event: ResizeEvent) => void>(
		(event) => {
			if (active) {
				onResize(elementId, event, resizeHandleRef.current);

				event.stopPropagation();
				event.preventDefault();
			}
		},
		[ elementId, active, onResize ]
	);

	const stopResize = useCallback<(event: ResizeEvent) => void>(
		(event) => {
			if (active) {
				setActive(false);
				onStopResize(elementId, event, resizeHandleRef.current);
			}
		},
		[ elementId, active, onStopResize ]
	);

	useEffect(() => {
		if (isFunction(registerElement)) {
			registerElement(elementId, {
				id: elementId,
				type: RESIZE_COMPONENT_TYPE.HANDLE,
				resizeDisabled: disabled,
				onDataUpdate: (allData) => {
					const splitterIndex = allData.findIndex(({ id }) => id === elementId);

					if (splitterIndex > -1) {
						const resizeDisabled = !!allData[splitterIndex]?.resizeDisabled;

						if (disabled !== resizeDisabled) {
							setDisabled(resizeDisabled);
						}
					}
				}
			});
		}

		return () => {
			if (isFunction(unregisterElement)) {
				unregisterElement(elementId);
			}
		};
	}, [ registerElement, unregisterElement, elementId, disabled ]);

	useEventListener(
		document,
		"touchend",
		(event) => {
			stopResize(event);
		},
		undefined,
		active && !disabled
	);

	useEventListener(
		document,
		"mouseup",
		(event) => {
			stopResize(event);
		},
		undefined,
		active && !disabled
	);

	useEventListener(
		document,
		"mousemove",
		(event) => {
			resize(event);
		},
		{ passive: false },
		active && !disabled
	);

	useEventListener(
		document,
		"touchmove",
		(event) => {
			resize(event);
		},
		{ passive: false },
		active && !disabled
	);

	const resizeHandleProps: IResizeHandleProps = {
		ref: resizeHandleRef,
		onMouseDown: startResize,
		onTouchStart: stopResize,
		"data-resize-handle-id": elementId
	};

	const resizeHandleData: IResizeHandleData = {
		active,
		disabled
	};

	return [ resizeHandleProps, resizeHandleData ];
}
